import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputLabel,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { CreateQarule, UpdateQarule } from "../../../queries/QAruleData";
import { getGroups } from "../../../queries/groups";
import { Coders, SearchWorkers } from "../../../queries/Workers";
import { CriteriaFilters, hiddenFilters } from "../../molecules/CriteriaFilters";
import { buildSearchFilter } from "../workItemsTable";
import { getUnrestrictedDictionary, fetchResolvedConfigurationValue, fetchRubric, fetchRubrics, fetchRubricVersions, } from "../../../utils/api";
import { CMX_TENANT_ID, policiesContext, serviceLineMapper } from "../../../utils/common";
import type { Rubric, RubricVersion } from "../../../types/rubric";
import CheckboxGroup from "../../atoms/checkboxes/checkboxes";
import { codeCheckboxes, defaultCQAConfig, multiUserProjectCheckboxes } from "./defaultCQAConfig";
import { QA_RULE_UPDATED_EVENT } from "../../molecules/ProjectSelectColumn";
import { Audience } from "./Audience/Audience";
import { LABEL_SPECIFIC_VOLUME } from "./Audience/constants";
import { AudienceConfig, DictionaryValue, OptionEntry, TaskConfig } from "../../../types/cqa";
import { processSubmittedAudience } from "../../../utils/cqa/assignmentUtil";
import { Tasks } from "./Tasks/Tasks";
import { DataSource, getDropdown } from "./DataSource/DataSource";
import { Definition } from "./Definition/Definition";

type QARuleVariables = {
  qarule: {
    name: string,
    type: string,
    description: string,
    group_id: string | undefined,
    manager: string | undefined,
    start_date: string,
    end_date: string | undefined,
    max_cases: string,
    filters: any,
    level: number | undefined,
    service: string,
    availability: string,
    frequency: string | undefined,
    predictability: string,
    sample_reason: string | undefined
    guideline: string | undefined,
    project_configuration: TaskConfig,
    assignee_ids: string[] | undefined
  },
  id?: string,
};

let onSearchController = new AbortController();
let abortSignal = onSearchController.signal

const INDIVIDUAL_USERS = "Individual Users";

function getDefaultOptions(isCqaSample) {
  return {
    recipient_type: [
      { label: "Group", value: "Group" },
      { label: "Manager", value: "Manager" },
    ],
    unit_of_measure: [
      { label: "Cases", value: "Cases" },
      { label: "Percentage", value: "Percentage" },
    ],
    frequency: [
      { label: "Daily", value: "Daily" },
      { label: "Weekly", value: "Weekly" },
      { label: "Monthly", value: "Monthly" },
      { label: "Yearly", value: "Yearly" },
    ],
    predictability: [
      { label: "Consistent", value: "consistent" },
      { label: "Random", value: "random" },
    ],
    recipient: [{ label: "Group", value: "Group" },]
  };
}

export const qaRuleLevels = {
  "case_samples": "1",
  "coder_samples": "0",
  "verification_samples": "2",
  "cqa_samples": "3"
}

const CODING_MANAGER = "coding_manager";

export const QARuleForm = (props: any) => {
  const { setShowDetails, client, directoryType, selected } = props;
  const isCqaSample = directoryType === "cqa_samples";

  //region Pre-paint Formatting
  /* Because of how CriteraFilters is designed, we need to format filters here instead of useEffect
     as when the component is mounted it will no longer look at "filters" but instead "uiFilters"
     within the component. So any delayed parent modifications won't be reflected */
  // If on Coder Samples, add default criteria filter for "Coder" //
  let formattedFilters = selected?.filters ?? [];
  if (directoryType === "coder_samples") {
    if (formattedFilters.length === 0) {
      formattedFilters = [
        {
          "static": true,
          "filterIndex": 0,
          "fieldId": "owner",
          "fieldName": "owner",
          "isFieldStatic": true,
          "operator": "eq",
          "value": null,
        }
      ];
    }
    else {
      const coderFilterIndex = formattedFilters.findIndex(filter => filter.fieldName === "owner");
      const filterCopy = [...formattedFilters];

      if (coderFilterIndex !== -1) {
        filterCopy[coderFilterIndex].static = true;
        filterCopy[coderFilterIndex].isFieldStatic = true;
      }

      if (coderFilterIndex > 0) {
        const coderFilter = filterCopy.splice(coderFilterIndex, 1);
        filterCopy.unshift(coderFilter[0]);
        filterCopy.forEach((filter, index) => filter.filterIndex = index);
      }
      else if (coderFilterIndex === -1) {
        filterCopy.unshift({
          "static": true,
          "filterIndex": 0,
          "fieldId": "owner",
          "fieldName": "owner",
          "isFieldStatic": true,
          "operator": "eq",
          "value": null,
        });
        filterCopy.forEach((filter, index) => filter.filterIndex = index);
      }

      formattedFilters = filterCopy;
    }
  }
  //endregion

  //region State
  const [name, setName] = React.useState(selected?.name ?? "");
  const [description, setDescription] = React.useState(selected?.description ?? "");

  const [snackBars, setSnackBars] = React.useState([]);
  const [snackBarsOpen, setSnackBarsOpen] = React.useState(false);

  const defaultOptions = getDefaultOptions(isCqaSample)

  const [optionsMap, setOptionsMap] = React.useState(defaultOptions);
  const [valuesMap, setValuesMap] = React.useState({});

  const [startDate, setStartDate] = React.useState(selected?.start_date?.split("T")[0] ?? "");
  const [endDate, setEndDate] = React.useState(selected?.end_date ? selected?.end_date?.split("T")[0] : "N/A");
  const [indefinite, setIndefinite] = React.useState(!selected?.end_date);
  const [availability, setAvailability] = React.useState(true);
  const [sampleReason, setSampleReason] = React.useState(selected?.sample_reason || "");
  const [guideline, setGuideline] = React.useState(selected?.guideline || "");
  const [cqaConfig, setCqaConfig] = React.useState<TaskConfig>(selected?.project_configuration ?? defaultCQAConfig);
  const [audienceConfig, setAudienceConfig] = React.useState<AudienceConfig>(selected?.project_configuration?.audience || {});
  const [isMultiUserProject, setIsMultiUserProject] = React.useState(selected?.assignee_ids?.length > 0);
  const isDetail = Object.keys(selected).length > 0;

  const [managers, setManagers] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [allCoders, setAllCoders] = React.useState([]);
  const [cqaCoders, setCQACoders] = React.useState([]);

  const [sampleReasons, setSampleReasons] = React.useState<DictionaryValue[]>([]);
  const [availableGuidelines, setAvailableGuidelines] = React.useState<string[]>([]);

  // Rubric state
  const [rubrics, setRubrics] = React.useState<Rubric[]>([]);
  const [rubricVersions, setRubricVersions] = React.useState<RubricVersion[]>([]);
  const [selectedRubricId, setSelectedRubricId] = React.useState<number | null>(selected?.project_configuration?.rubricId ?? null);
  const [selectedRubricVersionId, setSelectedRubricVersionId] = React.useState<number | null>(selected?.project_configuration?.rubricVersionId ?? null);

  const [filters, setFilters] = React.useState(formattedFilters);
  const [loading, setLoading] = React.useState(true);
  const [spanAnnotationEnabled, setSpanAnnotationEnabled] = React.useState(false);
  const [multiUserEnabled, _] = React.useState(false);
  const { userContext } = React.useContext(policiesContext);
  //endregion
  const is_recipient_type_individuals = valuesMap["recipient_type"]?.label == 'Individual Users'

  //region Helpers
  const setOptions = (optionName, options) => {
    setOptionsMap((prevOptionsMap) => {
      const newOptions = { ...prevOptionsMap, [optionName]: options };
      return newOptions;
    });
  };

  const setValues = (valueName: string, value: {
    label: string,
    value: string
  }) => {
    let newValues = { ...valuesMap };
    newValues[valueName] = value;

    if (valueName === "recipient_type") {
      switch (value.value) {
        case "Manager":
          setOptions("recipient", managers);
          newValues["recipient"] = { label: "", value: undefined }
          break;
        default:
          setOptions("recipient", groups)
          newValues["recipient"] = { label: "", value: undefined }
      }
      ;
    } else if (valueName === "unit_of_measure") {
      newValues["volume"] = { label: "", value: undefined };
      // predictability can only be consistent if unit_of_measure is cases
      setOptions(
        "predictability",
        value.value === "Cases"
          ? defaultOptions.predictability.filter(
            (itm) => itm.value === "consistent"
          )
          : defaultOptions.predictability
      );
      if (
        value.value === "Cases" &&
        newValues["predictability"]?.value === "random"
      )
        newValues["predictability"] = {
          label: "Consistent",
          value: "consistent",
        };
    }
    setValuesMap(newValues);
  }

  const setMultiValue = (valueName: string, value: {
    label: string,
    value: string
  }[]) => {
    let newValues = { ...valuesMap };
    newValues[valueName] = value;

    setValuesMap(newValues);
  }

  const updateValuesMapGroup = (value: OptionEntry) => {
    let newValues = { ...valuesMap };
    newValues["recipient"] = value;
    newValues["recipient_type"] = { label: value == null ? "" : "Group", value: value == null ? undefined : "Group" }
    setOptions("recipient", groups)
    setValuesMap(newValues);
  }

  const createQARule = async () => {
    // @ts-ignore
    window.DD_RUM && window.DD_RUM.startView("Open QA audit create");

    let errors = [];

    // CQA Specific Checks //
    if (isCqaSample && !sampleReason) {
      errors.push("Please select a purpose for the project");
    }

    if (isCqaSample && !guideline) {
      errors.push("Please select a guideline");
    }

    if (isCqaSample && codeCheckboxes.every(checkbox => cqaConfig[checkbox.key] == false)) {
      errors.push("Please select at least one code to review");
    }

    if (audienceConfig && audienceConfig.individuals?.length > 0) {
      if (!audienceConfig.volumeType && audienceConfig.distributionVolume === LABEL_SPECIFIC_VOLUME) {
        errors.push("Please select audience volume type");
      }
    }
    /////////////////////////

    if (isMultiUserProject) {
      if (!valuesMap["auditors"]?.length) {
        errors.push("Please select an auditor");
      }
      if (!valuesMap["coders"]?.length) {
        errors.push("Please select a coder");
      }
    } else {
      // cqa settings will no longer use recipient_type
      if (!isCqaSample) {
        if (!valuesMap["recipient_type"]?.value) {
          errors.push("Please select a recipient type");
        }

        const recipient = valuesMap["recipient"];

        if ((!Array.isArray(recipient) && !recipient?.value) ||
          (Array.isArray(recipient) && recipient.length == 0)) {
          errors.push("Please select a recipient");
        }
      }
    }

    if (!startDate) {
      errors.push("Please select a start date");
    }
    if (!valuesMap["volume"]?.value) {
      errors.push("Please select a volume");
    }
    if (!name) {
      errors.push("Please enter a name");
    }
    if (!indefinite && endDate === "N/A") {
      errors.push("Please enter an end date or set to \"indefinite\"");
    }
    if (directoryType === "coder_samples") {
      if (!filters[0]?.value) {
        errors.push("Please select a coder filter from the dropdown");
      }
    }
    if (filters.findIndex(filter => filter.fieldName === "automate_status") === -1) {
      errors.push("Please select a processing method");
    }
    else if (filters.findIndex(filter => filter.fieldName === "automate_status" && filter.value.length === 0) !== -1) {
      errors.push("Please select at least one processing method");
    }

    if (filters.findIndex(filter => filter.fieldName === "automate_status" && filter.value.length === 1 && filter.value[0] === "AUTOMATED") !== -1) {
      if (filters.findIndex(filter => filter.fieldName === "owner") !== -1) {
        errors.push("Please remove the coder filter when selecting automated processing or add manual processing.");
      }
    }

    if (!valuesMap["predictability"]?.value) {
      errors.push("Please select predictability option");
    }

    // If a rubric is selected, a rubric version must also be selected
    if (isCqaSample && selectedRubricId && !selectedRubricVersionId) {
      errors.push("Please select a workflow collection version");
    }

    if (errors.length === 0) {
      let filtersCopy = [...filters];

      // If creating verification sample add hidden filter for QA status //
      if (directoryType === "verification_samples") {
        if (filtersCopy.findIndex(filter => filter.fieldName === "qa_level") === -1) {
          filtersCopy.push({
            "filterIndex": filtersCopy.length,
            "fieldId": "qa_level",
            "join": filtersCopy.length === 0 ? undefined : "AND",
            "fieldName": "qa_level",
            "operator": "gt",
            "value": -1
          });
        }
        else {
          // Move QA status filter to the end of the array //
          const qaFilterIndex = filters.findIndex(filter => filter.fieldName === "qa_level");
          const qaFilter = filtersCopy.splice(qaFilterIndex, 1);
          filtersCopy.push(qaFilter[0]);
          filtersCopy.forEach((filter, index) => filter.filterIndex = index);
        }
      }

      // Move all hidden filters to the beginning of the array //
      hiddenFilters.forEach(hiddenFilter => {
        const hiddenFilterIndex = filtersCopy.findIndex(filter => filter.fieldName === hiddenFilter);
        if (hiddenFilterIndex !== -1) {
          const hiddenFilter = filtersCopy.splice(hiddenFilterIndex, 1);
          filtersCopy.unshift(hiddenFilter[0]);
        }
      });

      // Parse through filters and ensure "join" has a value //
      filtersCopy.forEach((filter, index) => {
        if (index === 0) {
          filter.join = undefined;
        } else {
          filter.join = filter.join ?? "AND";
        }
      });

      // Update filterIndex to match array index + 1 //
      filtersCopy.forEach((filter, index) => filter.filterIndex = index + 1);

      const currentDate = new Date();
      const volumeTail = valuesMap["unit_of_measure"]?.value === "Percentage" ? "%" : "";

      let assignee_ids = undefined;
      let project_configuration: TaskConfig = {
        ...cqaConfig,
        rubricId: selectedRubricId,
        rubricVersionId: selectedRubricVersionId
      };
      if (isMultiUserProject) {
        assignee_ids = valuesMap["coders"]?.map(coder => coder.value);
        project_configuration = { ...project_configuration, auditors: valuesMap["auditors"]?.map(auditor => auditor.value) }
      } else {
        project_configuration = { ...project_configuration, auditors: undefined }
      }
      project_configuration = { ...project_configuration, audience: audienceConfig }

      let variables: QARuleVariables = {
        qarule: {
          name,
          type: "QARULE_COLLECTION",
          description,
          group_id: valuesMap["recipient_type"]?.value === "Group" && !isMultiUserProject ? valuesMap["recipient"]?.value : null,
          manager: valuesMap["recipient_type"]?.value === "Manager" && !isMultiUserProject ? valuesMap["recipient"]?.value : null,
          assignee_ids: assignee_ids ?? null,
          start_date: new Date(`${startDate} ${currentDate.getHours()}:${currentDate.getMinutes()}`).toISOString(),
          end_date: endDate !== "N/A" ? new Date(`${endDate} ${currentDate.getHours()}:${currentDate.getMinutes()}`).toISOString() : null,
          max_cases: valuesMap["volume"]?.value + volumeTail,
          filters: [{ rules: buildSearchFilter(filtersCopy) }],
          level: qaRuleLevels[directoryType] ? parseInt(qaRuleLevels[directoryType]) : undefined,
          service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
          availability: availability ? "AVAILABLE" : "UNAVAILABLE",
          frequency: valuesMap["frequency"]?.value ?? null,
          predictability: valuesMap["predictability"]?.value,
          sample_reason: sampleReason,
          guideline: guideline,
          project_configuration: project_configuration
        },
      };

      if (isDetail) {
        variables = {
          ...variables,
          id: selected.collection_id
        }
      }

      if (isCqaSample) {
        const groupId = valuesMap["recipient_type"]?.value === "Group" && !isMultiUserProject ? valuesMap["recipient"]?.value : null;
        const errorsMaybe = await processSubmittedAudience(audienceConfig, selected.id, groupId);
        errors = [...errors, ...errorsMaybe]
      }

      // Only stop mutate call if cqa settings has errors, otherwise make the mutate call.
      if (!isCqaSample || errors.length === 0) {
        await props?.client
          ?.mutate({
            mutation: isDetail ? UpdateQarule : CreateQarule,
            variables: variables,
            fetchPolicy: "no-cache",
          })
          .then((response: any) => {
            // Notify ProjectSelectColumn to refresh and pick up updated rubric version
            window.dispatchEvent(new CustomEvent(QA_RULE_UPDATED_EVENT));
          })
          .catch((err) => {
            errors.push(`Error updating or creating rule`);
            console.error(err);
          });
      }
    }

    if (errors.length == 0) {
      setShowDetails(false);
    } else {
      setSnackBars(errors);
      setSnackBarsOpen(true);
    }
  };

  const getCoders = async () => {
    const variables = { pageNumber: 0, pageSize: 200 };
    try {
      const codersResponse = await props?.client.query({
        query: Coders,
        variables: variables,
        fetchPolicy: "no-cache",
      });
      const coders = codersResponse.data.Coders.data.sort((a, b) => a?.data?.last_name.localeCompare(b?.data?.last_name));
      return coders;
    }
    catch (err) {
      console.error(`Error fetching coders: ${err}`);
      return [];
    }
  }

  const getManagersAndGroups = async () => {
    const variables = { pageNumber: 0, pageSize: 100 };
    try {
      const codersResponse = await props?.client.query({
        query: Coders,
        variables: variables,
        fetchPolicy: "no-cache",
      })
      const newManagers = codersResponse.data.Coders.data.filter(coder => coder.data.roles.some(role => role.name === CODING_MANAGER || role.name === "cqa_manager"))
        .sort((a, b) => a?.data?.last_name.localeCompare(b?.data?.last_name)).map(manager => {
          return {
            label: manager.data.first_name + ' ' + manager.data.last_name,
            value: manager.data.worker_id
          }
        })

      const groupsResponse = await props?.client.query({
        query: getGroups,
        fetchPolicy: "no-cache",
      });
      const newGroups = [];
      groupsResponse?.data?.Groups?.data?.forEach((group) => {
        newGroups.push({
          label: group?.data?.name,
          value: group?.data?.group_id,
        });
      });

      return { managers: newManagers, groups: newGroups };
    }
    catch (err) {
      console.error(`Error fetching managers and groups: ${err}`);
      return { managers: [], groups: [] };
    }
  }

  const getWorkers = async (worker_ids: string[]) => {

    try {
      const variables = {
        request: {
          combinator: 'AND',
          filters: [
            {
              key: 'worker_id',
              operation: 'in',
              value: worker_ids,
            },
          ],
          pagination: {
            limit: 1000,
            offset: 0,
          },
        }
      };

      const codersResponse = await props?.client.query({
        query: SearchWorkers,
        variables: variables,
        fetchPolicy: "no-cache",
      })

      return codersResponse.data.SearchWorker.data.map(worker => {
        return {
          label: worker.data.first_name + ' ' + worker.data.last_name,
          value: worker.data.worker_id
        }
      })
    }
    catch (err) {
      console.error(`Error fetching workers: ${err}`);
      return [];
    }
  }

  const getDictionaryRowValues = async (dictionaryName: string): Promise<DictionaryValue[]> => {
    try {
      const dictionary = await getUnrestrictedDictionary(CMX_TENANT_ID, dictionaryName, abortSignal);
      return dictionary.rowValues
    }
    catch (e) {
      console.error(`Error fetching sample reasons, please contact support!`, e);
      return [];
    }
  }
  //endregion

  //region Lifecycle
  useEffect(() => {
    const setupValuesAndOptions = async () => {
      if (isDetail) {
        const frequency = selected.frequency;
        const volume = `${parseInt(selected.max_cases)}`
        const volumeType = selected.max_cases[selected.max_cases.length - 1] === '%'
          ? "Percentage"
          : "Cases";

        let recipient = { value: selected.manager?.value, label: selected.manager?.label };
        let recipientType = "Manager";
        if (!selected.manager && selected.group_id) {
          recipient = { value: selected.group_id?.value, label: selected.group_id?.label }
          recipientType = "Group"
        }


        let coders = []
        if (
          selected?.assignee_ids
        ) {
          coders = await getWorkers(selected.assignee_ids);
        }

        let auditors = []
        if (
          selected?.project_configuration?.auditors
        ) {
          auditors = await getWorkers(selected.project_configuration.auditors);
        }


        const predictability = selected.predictability;
        return {
          "frequency": { label: frequency, value: frequency },
          "volume": { label: volume, value: volume },
          "unit_of_measure": { label: volumeType, value: volumeType },
          "recipient_type": { label: recipientType, value: recipientType },
          "recipient": recipient,
          "coders": coders,
          "auditors": auditors,
          "predictability": predictability ? defaultOptions.predictability?.find(itm => itm.value === predictability) : { label: "Consistent", value: "consistent" }
        };
      }

      return {
        "predictability": { label: "Consistent", value: "consistent" }
      };
    }

    const init = async () => {
      setLoading(true);

      // Fetch span_annotation_enabled configuration
      if (userContext?.activeContext?.tenantId && userContext?.serviceLine?.name) {
        try {
          const value = await fetchResolvedConfigurationValue(
            "span_annotation_enabled",
            userContext.activeContext.tenantId,
            userContext.serviceLine.name
          );
          setSpanAnnotationEnabled(value === "true");
        } catch (err) {
          console.error("Error fetching span_annotation_enabled configuration:", err);
          setSpanAnnotationEnabled(false);
        }
      }

      const sampleReasons = isCqaSample ? await getDictionaryRowValues("CQA Reasons") : [];
      const guidelines = isCqaSample ? (await getDictionaryRowValues("CQA Guidelines")).map(guideline => guideline.Guideline ?? "").filter(value => value.length) : [];

      const coders = await getCoders();
      const cqaCoders = coders.filter(coder =>
        coder.data.roles.some(role => ['cqa_coder', 'cqa_manager'].includes(role.name))
      ).map(coder => {
        return {
          label: coder.data.first_name + ' ' + coder.data.last_name,
          value: coder.data.worker_id
        }
      })

      const { managers, groups } = await getManagersAndGroups();

      let recipientType = "Manager";
      let recipient = managers
      if (!selected.manager && selected.group_id) {
        recipientType = "Group"
        recipient = groups
      }

      const valuesMap = await setupValuesAndOptions();

      setValuesMap(valuesMap);

      setOptions("auditors", cqaCoders.slice())
      setOptions("coders", cqaCoders.slice())
      setOptions("recipient", recipient)

      setOptions(
        "predictability",
        selected?.max_cases?.length && selected.max_cases[selected.max_cases.length - 1] === '%'
          ? defaultOptions.predictability
          : [{ label: "Consistent", value: "consistent" }]
      );
      setManagers(managers);
      setGroups(groups);
      setAllCoders(coders);
      setCQACoders(cqaCoders)

      setSampleReasons(sampleReasons)

      setAvailableGuidelines(guidelines)

      // Fetch rubrics for CQA samples
      if (isCqaSample) {
        try {
          const rubricsData = await fetchRubrics(true); // active only
          
          // Include existing rubric if it's inactive (for editing existing projects)
          const existingRubricId = selected?.project_configuration?.rubricId;
          if (existingRubricId && !rubricsData.some(r => r.id === existingRubricId)) {
            try {
              const existingRubric = await fetchRubric(existingRubricId);
              rubricsData.push(existingRubric);
            } catch {
              console.warn(`Could not fetch inactive rubric ${existingRubricId}`);
            }
          }
          
          setRubrics(rubricsData);
        } catch (err) {
          console.error("Error fetching rubrics:", err);
        }
      }

      setLoading(false);
    }

    init();
  }, [selected, userContext?.activeContext?.tenantId, userContext?.serviceLine?.name]);

  // Fetch rubric versions when selectedRubricId changes
  useEffect(() => {
    const loadVersions = async () => {
      if (selectedRubricId) {
        try {
          const versionsData = await fetchRubricVersions(selectedRubricId);
          const activeVersions = versionsData.filter(v => v.active !== false);
          
          // Include existing version if it's inactive (for editing existing projects)
          const existingVersionId = selected?.project_configuration?.rubricVersionId;
          if (existingVersionId && !activeVersions.some(v => v.id === existingVersionId)) {
            const inactiveVersion = versionsData.find(v => v.id === existingVersionId);
            if (inactiveVersion) {
              activeVersions.push(inactiveVersion);
            }
          }
          
          setRubricVersions(activeVersions);
        } catch (err) {
          console.error("Error fetching rubric versions:", err);
          setRubricVersions([]);
        }
      } else {
        setRubricVersions([]);
      }
    };

    loadVersions();
  }, [selectedRubricId, selected?.project_configuration?.rubricVersionId]);

  //endregion

  const onSampleReasonClick = (e, value) => {
    setSampleReason(e.target.value)

    const dictionaryValue = sampleReasons.find((dictionaryValue) => dictionaryValue['Reason'] == e.target.value)
    if (dictionaryValue) {
      setCqaConfig({ ...cqaConfig, assessment: dictionaryValue['Assessment'] == 'TRUE', role: dictionaryValue['Role'] == 'TRUE' })
    }
  }

  return (
    <>
      <Grid>
        <Box>
          <Grid container marginBottom={"20px"}>
            <Grid item xs={6} alignSelf="center">
              <Typography variant="h2">
                {isDetail ? 'Update ' : 'Create '}
                {props?.directoryType === "verification_samples" && "Verification Sample"}
                {props?.directoryType === "coder_samples" && "Coder Sample"}
                {props?.directoryType === "case_samples" && "Case Sample"}
                {props?.directoryType === "cqa_samples" && "Quality Assessment Project"}
              </Typography>
            </Grid>
            <Grid
              item
              container
              xs={6}
              alignItems="flex-end"
              justifyContent={"flex-end"}
              gap="20px"
            >
              <>
                <Grid item>
                  <Button onClick={() => setShowDetails(false)}>Cancel</Button>
                </Grid>
                <Grid item>
                  <Button onClick={() => createQARule()} variant="contained"
                    disabled={loading}
                  >
                    {isDetail ? 'Update' : 'Create'}
                  </Button>
                </Grid>
              </>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ marginTop: "20px", marginBottom: "20px" }} />

        {props?.directoryType === "verification_samples" && (
          <Box padding={"6px"} margin={"0 0 12px 0"} flex={1} sx={{ background: "#E0E8FF", borderRadius: "3px" }}>
            <Typography
              fontWeight={400}
              sx={{ color: "#203D48", fontSize: "12px" }}
            >
              Verification samples are collected from cases that have previously been selected for quality review by a
              manager.
            </Typography>
          </Box>
        )}

        {props?.directoryType === "coder_samples" && (
          <Box padding={"6px"} margin={"0 0 12px 0"} flex={1} sx={{ background: "#E0E8FF", borderRadius: "3px" }}>
            <Typography
              fontWeight={400}
              sx={{ color: "#203D48", fontSize: "12px" }}
            >
              Coder samples require a coder filter to be selected. Additional filters can be added to further
              refine the selection.
            </Typography>
          </Box>
        )}

        <Grid container>
          <Grid container flexDirection={"column"} item xs={12} md={7} sx={{ minWidth: 0, flexWrap: "nowrap" }}>
            <Grid item>
              <Definition
                availableGuidelines={availableGuidelines}
                description={description}
                endDate={endDate}
                guideline={guideline}
                indefiniteTargetDate={indefinite}
                isCqaSample={isCqaSample}
                loading={loading}
                rubricVersions={rubricVersions}
                rubrics={rubrics}
                ruleName={name}
                sampleReason={sampleReason}
                sampleReasons={sampleReasons}
                selectedRubricId={selectedRubricId}
                selectedRubricVersionId={selectedRubricVersionId}
                startDate={startDate}
                onSampleReasonClick={onSampleReasonClick}
                setDescription={setDescription}
                setEndDate={setEndDate}
                setGuideline={setGuideline}
                setIndefiniteTargetDate={setIndefinite}
                setName={setName}
                setSelectedRubricId={setSelectedRubricId}
                setSelectedRubricVersionId={setSelectedRubricVersionId}
                setStartDate={setStartDate}
              />
            </Grid>
            {
              isCqaSample && (
                <Grid item>
                  <Audience
                    config={audienceConfig}
                    codersOptions={
                      allCoders?.map(worker => {
                        return {
                          label: worker.data.first_name + ' ' + worker.data.last_name,
                          value: worker.data.worker_id
                        }
                      })
                    }
                    codersSelected={valuesMap["coders"]}
                    groupOptions={groups}
                    projectId={selected.id}
                    setConfig={setAudienceConfig}
                    setErrorMessages={setSnackBars}
                    setErrorOpen={setSnackBarsOpen}
                  />
                </Grid>
              )
            }
            {isCqaSample && (
              <Grid item>
                <Tasks cqaConfig={cqaConfig} spanAnnotationEnabled={spanAnnotationEnabled} setCqaConfig={setCqaConfig} selectedRubricId={selectedRubricId} />
              </Grid>
            )}
            <Grid item>
              <DataSource
                isCqaSample={isCqaSample}
                optionsMap={optionsMap}
                valuesMap={valuesMap}
                setMultiValue={setMultiValue}
                setValues={setValues}
              />
            </Grid>
            {/*Reviewer Type and Reviewer*/}
            <Grid container gap={"24px"} width={"90%"} sx={{marginTop: "10px", marginBottom: { xs: "22px", md: "50px" }}}>
              {isCqaSample && multiUserEnabled && (
                <div>
                  <InputLabel htmlFor="qa-multi-user-project">
                    <Typography variant="caption2">Multi-User Project</Typography>
                  </InputLabel>
                  <CheckboxGroup
                    fields={multiUserProjectCheckboxes}
                    values={{ isMultiUserProject }}
                    onChange={(field, checked) => {
                      setIsMultiUserProject(checked);
                    }}
                  />
                </div>)}
              {isCqaSample && isMultiUserProject ?
                <Grid container gap={"24px"} width={"90%"}>
                  {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "auditors", "Auditors", false, true, true)}
                  {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "coders", "Assignees", false, true, true)}
                </Grid> : !isCqaSample ?
                  <Grid container gap={"24px"} width={"90%"}>
                    {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "recipient_type", "Reviewer Type")}
                    {getDropdown(setValues, setMultiValue, optionsMap, valuesMap, "recipient", "Reviewer", false, true, is_recipient_type_individuals)}
                  </Grid> : null
              }
            </Grid>
          </Grid>

          {/*Filters*/}
          <Grid item xs={12} md={5} sx={{ padding: { xs: "0px", md: "12px" }, marginTop: { xs: "0px", md: "20px" } }}>
            {loading ? (
              <Box sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}>
                <CircularProgress color="primary" size={20} />
              </Box>
            ) : (
              <Box sx={{ display: loading ? "none" : "" }}>
                <CriteriaFilters
                  filters={filters}
                  setFilters={setFilters}
                  client={client}
                  allCoders={allCoders}
                  allGroups={groups}
                  directoryType={directoryType}
                  setSnackBars={setSnackBars}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>

      {snackBars[0] && (
        <Snackbar
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          open={snackBarsOpen}
          onClose={() => setSnackBarsOpen(false)}
          sx={{ marginLeft: '100px' }}
        >
          <Alert severity={"error"} sx={{ width: "100%" }}>
            {snackBars[0]}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};